import { mui } from "@/mui";
import { UserIcon, Palette, CircleDot, EyeOff, XCircle, Wifi, WifiOff, Mic, Video, Database, ShieldCheck, CheckCircle2, AlertCircle, XCircle as XCircleIcon2 } from "lucide-react";
import type { ReactNode } from "react";
import { useState, useEffect } from "react";
import { useSession, useMobyRepo, useMobyNode } from "./provider";
import { toast } from "sonner";

type PermissionStatus = 'granted' | 'denied' | 'prompt' | 'unknown';

interface PermissionsState {
  microphone: PermissionStatus;
  camera: PermissionStatus;
  persistentStorage: PermissionStatus;
}

// Color palette for avatar backgrounds
const AVATAR_COLORS = [
  "#E53935", // Red
  "#D81B60", // Pink
  "#8E24AA", // Purple
  "#5E35B1", // Deep Purple
  "#3949AB", // Indigo
  "#1E88E5", // Blue
  "#039BE5", // Light Blue
  "#00ACC1", // Cyan
  "#00897B", // Teal
  "#43A047", // Green
  "#7CB342", // Light Green
  "#C0CA33", // Lime
  "#FDD835", // Yellow
  "#FFB300", // Amber
  "#FB8C00", // Orange
  "#F4511E", // Deep Orange
];

// Emoji options for avatar decoration
const EMOJI_OPTIONS = [
  "", // None
  "😀", "😎", "🤓", "😊", "🥳", "🐳",
  "🚀", "💻", "🎮", "🎨", "🎵", "🍀",
  "⚡", "🔥", "💎", "🌟", "✨", "☄️",
];

type PresenceType = "online" | "invisible" | "dnd";

export function UserPreferencesDialog({ children }: { children: ReactNode }) {
  const session = useSession();
  const repo = useMobyRepo();
  const node = useMobyNode();
  const [open, setOpen] = useState(false);
  
  // Local state
  const [displayName, setDisplayName] = useState(session?.name || "");
  const [avatarColor, setAvatarColor] = useState(getSessionPreference("avatarColor") || "#546E7A");
  const [avatarEmoji, setAvatarEmoji] = useState(getSessionPreference("avatarEmoji") || "");
  const [bannerColor, setBannerColor] = useState(getSessionPreference("bannerColor") || (window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "#263238" : "#B0BEC5"));
  const [presence, setPresence] = useState<PresenceType>(getSessionPreference("presence") || "online");
  const [perms, setPerms] = useState<PermissionsState>({ microphone: 'unknown', camera: 'unknown', persistentStorage: 'unknown' });

  // Load preferences and permissions when dialog opens
  useEffect(() => {
    if (open && session) {
      setDisplayName(session.name);
      setAvatarColor(getSessionPreference("avatarColor") || "#546E7A");
      setAvatarEmoji(getSessionPreference("avatarEmoji") || "");
      setBannerColor(getSessionPreference("bannerColor") || (window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "#263238" : "#B0BEC5"));
      setPresence(getSessionPreference("presence") || "online");
      // Check current permission states
      checkPermissions();
    }
  }, [open, session]);

  const checkPermissions = async () => {
    const state: PermissionsState = { microphone: 'unknown', camera: 'unknown', persistentStorage: 'unknown' };
    try {
      const mic = await navigator.permissions.query({ name: 'microphone' as PermissionName });
      state.microphone = mic.state as PermissionStatus;
    } catch { state.microphone = 'unknown'; }
    try {
      const cam = await navigator.permissions.query({ name: 'camera' as PermissionName });
      state.camera = cam.state as PermissionStatus;
    } catch { state.camera = 'unknown'; }
    try {
      if (navigator.storage?.persisted) {
        const persisted = await navigator.storage.persisted();
        state.persistentStorage = persisted ? 'granted' : 'prompt';
      }
    } catch { state.persistentStorage = 'unknown'; }
    setPerms(state);
  };

  const requestMic = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach(t => t.stop());
      setPerms(p => ({ ...p, microphone: 'granted' }));
      toast.success('Microphone access granted');
    } catch {
      setPerms(p => ({ ...p, microphone: 'denied' }));
      toast.error('Microphone access denied');
    }
  };

  const requestCam = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      stream.getTracks().forEach(t => t.stop());
      setPerms(p => ({ ...p, camera: 'granted' }));
      toast.success('Camera access granted');
    } catch {
      setPerms(p => ({ ...p, camera: 'denied' }));
      toast.error('Camera access denied');
    }
  };

  const requestStorage = async () => {
    try {
      if (navigator.storage?.persist) {
        const granted = await navigator.storage.persist();
        setPerms(p => ({ ...p, persistentStorage: granted ? 'granted' : 'prompt' }));
        if (granted) {
          localStorage.setItem('moby_persistent_storage', 'granted');
          toast.success('Persistent storage granted');
        } else {
          toast.warning('Persistent storage not yet granted — try bookmarking the site');
        }
      }
    } catch {
      toast.error('Failed to request persistent storage');
    }
  };

  const getPermIcon = (status: PermissionStatus) => {
    switch (status) {
      case 'granted': return <CheckCircle2 size={16} className="text-green-500" />;
      case 'denied': return <XCircleIcon2 size={16} className="text-error" />;
      case 'prompt': return <AlertCircle size={16} className="text-amber-500" />;
      default: return <AlertCircle size={16} className="text-on-surface-variant/50" />;
    }
  };

  const getPermLabel = (status: PermissionStatus) => {
    switch (status) {
      case 'granted': return 'Granted';
      case 'denied': return 'Denied';
      case 'prompt': return 'Not set';
      default: return 'Unknown';
    }
  };

  const handleSave = async () => {
    if (!session) return;

    try {
      const nameChanged = displayName !== session.name && displayName.trim().length > 0;
      
      // Update display name if changed
      if (nameChanged) {
        await repo.setSession({
          ...session,
          name: displayName.trim(),
        });
        
        // CRITICAL: Wait for IndexedDB transaction to fully commit
        // This ensures the session update is persisted before UI updates
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // Save preferences to localStorage
      setSessionPreference("avatarColor", avatarColor);
      setSessionPreference("avatarEmoji", avatarEmoji);
      setSessionPreference("bannerColor", bannerColor);
      setSessionPreference("presence", presence);

      // Emit event for UI updates - this triggers reactive updates across all components
      // The useSession() hook uses useSuspenseLiveQuery which monitors the DB and auto-updates
      window.dispatchEvent(new CustomEvent("user-preferences-updated", {
        detail: { 
          avatarColor, 
          avatarEmoji, 
          bannerColor, 
          presence,
          nameChanged // Signal that name changed so components can refresh if needed
        }
      }));

      // Broadcast profile update to all connected peers
      // This sends PROFILE_INFO packets with the updated preferences
      try {
        await node.broadcastProfileUpdate();
        console.log("Profile update broadcast to all connected peers");
      } catch (broadcastError) {
        console.warn("Failed to broadcast profile update to peers:", broadcastError);
        // Don't fail the save if broadcast fails - local changes are saved
      }

      toast.success("Preferences saved!");
      setOpen(false);
      
      // NO PAGE RELOAD: Session name is just display text, doesn't affect address/secret
      // The useSuspenseLiveQuery in useSession() will automatically trigger re-renders
      // when the session changes in the database. Reloading was causing data loss.
    } catch (error) {
      console.error("Failed to save preferences:", error);
      toast.error("Failed to save preferences");
    }
  };

  const getAbbreviation = () => {
    return displayName.slice(0, 2).toUpperCase() || "AA";
  };

  const getPresenceBadge = () => {
    switch (presence) {
      case "online":
        return <div className="absolute -bottom-1 -right-1 bg-green-500 rounded-full w-3 h-3 border-2 border-surface-container-high" />;
      case "invisible":
        return null; // No badge for invisible
      case "dnd":
        return (
          <div className="absolute -bottom-1 -right-1 bg-black rounded-full w-3 h-3 border-2 border-surface-container-high flex items-center justify-center">
            <XCircle size={8} className="text-white" strokeWidth={3} />
          </div>
        );
    }
  };

  return (
    <mui.Dialog.Root open={open} onOpenChange={setOpen}>
      {children}
      <mui.Dialog.Portal>
        <mui.Dialog.Backdrop />
        <mui.Dialog.Popup className="max-w-md max-h-[90vh] flex flex-col">
          <mui.Dialog.Title>User Preferences</mui.Dialog.Title>
          <mui.Dialog.Description>
            Customize your profile appearance and settings
          </mui.Dialog.Description>

          <div className="space-y-6 mt-6 overflow-y-auto flex-1 px-1">
            {/* Avatar Preview */}
            <div className="flex items-center justify-center">
              <div className="relative">
                <div 
                  className="w-24 h-24 rounded-full flex items-center justify-center text-white text-2xl font-bold"
                  style={{ backgroundColor: avatarColor }}
                >
                  <span>{getAbbreviation()}</span>
                  {avatarEmoji && (
                    <span className="absolute text-3xl" style={{ top: '-4px', right: '-4px' }}>
                      {avatarEmoji}
                    </span>
                  )}
                </div>
                {getPresenceBadge()}
              </div>
            </div>

            {/* Display Name */}
            <div className="relative">
              <mui.Input
                type="text"
                id="display-name"
                name="display-name"
                filled
                variant="surface"
                className="truncate"
                autoComplete="off"
                maxLength={32}
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
              <mui.FloatingLabel variant="surface" htmlFor="display-name">
                Display Name
              </mui.FloatingLabel>
            </div>

            {/* Avatar Color Picker */}
            <div>
              <label className="text-body-sm text-on-surface-variant mb-2 block">
                Avatar Color
              </label>
              <div className="grid grid-cols-8 gap-2">
                {AVATAR_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setAvatarColor(color)}
                    className="w-4 h-4 rounded-full border-2 hover:scale-110 transition-transform"
                    style={{
                      backgroundColor: color,
                      borderColor: avatarColor === color ? "#fff" : "transparent",
                      boxShadow: avatarColor === color ? "0 0 0 2px currentColor" : "none"
                    }}
                    title={color}
                  />
                ))}
              </div>
            </div>

            {/* Avatar Emoji */}
            <div>
              <label className="text-body-sm text-on-surface-variant mb-2 block">
                Avatar Emoji (Optional)
              </label>
              <div className="grid grid-cols-10 gap-2">
                {EMOJI_OPTIONS.map((emoji, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setAvatarEmoji(emoji)}
                    className={`w-8 h-8 rounded-lg flex items-center justify-center text-xl hover:bg-on-surface-variant/8 transition ${
                      avatarEmoji === emoji ? "bg-primary/10 ring-2 ring-primary" : ""
                    }`}
                    title={emoji || "None"}
                  >
                    {emoji || "—"}
                  </button>
                ))}
              </div>
            </div>

            {/* Banner Color Picker */}
            <div>
              <label className="text-body-sm text-on-surface-variant mb-2 block">
                Chat Banner Color
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={bannerColor}
                  onChange={(e) => setBannerColor(e.target.value)}
                  className="w-16 h-10 rounded border border-outline cursor-pointer"
                />
                <div 
                  className="flex-1 h-10 rounded border border-outline"
                  style={{ backgroundColor: bannerColor }}
                />
                <span className="text-body-sm text-on-surface-variant font-mono">
                  {bannerColor}
                </span>
              </div>
            </div>

            {/* Permissions Status */}
            <div>
              <label className="text-body-sm text-on-surface-variant mb-2 block">
                Permissions
              </label>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-2 rounded-lg border border-outline-variant">
                  <div className="flex items-center gap-2">
                    <Mic size={16} className="text-on-surface-variant" />
                    <span className="text-body-sm">Microphone</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {getPermIcon(perms.microphone)}
                    <span className="text-body-sm text-on-surface-variant w-16 text-right">{getPermLabel(perms.microphone)}</span>
                    {perms.microphone !== 'granted' && (
                      <button
                        type="button"
                        onClick={requestMic}
                        className="text-body-sm text-primary hover:underline ml-1"
                      >
                        Grant
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between p-2 rounded-lg border border-outline-variant">
                  <div className="flex items-center gap-2">
                    <Video size={16} className="text-on-surface-variant" />
                    <span className="text-body-sm">Camera</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {getPermIcon(perms.camera)}
                    <span className="text-body-sm text-on-surface-variant w-16 text-right">{getPermLabel(perms.camera)}</span>
                    {perms.camera !== 'granted' && (
                      <button
                        type="button"
                        onClick={requestCam}
                        className="text-body-sm text-primary hover:underline ml-1"
                      >
                        Grant
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex items-center justify-between p-2 rounded-lg border border-outline-variant">
                  <div className="flex items-center gap-2">
                    <Database size={16} className="text-on-surface-variant" />
                    <span className="text-body-sm">Persistent Storage</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {getPermIcon(perms.persistentStorage)}
                    <span className="text-body-sm text-on-surface-variant w-16 text-right">{getPermLabel(perms.persistentStorage)}</span>
                    {perms.persistentStorage !== 'granted' && (
                      <button
                        type="button"
                        onClick={requestStorage}
                        className="text-body-sm text-primary hover:underline ml-1"
                      >
                        Grant
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Presence Selector */}
            <div>
              <label className="text-body-sm text-on-surface-variant mb-2 block">
                Presence Status
              </label>
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => setPresence("online")}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border transition ${
                    presence === "online"
                      ? "border-primary bg-primary/10"
                      : "border-outline hover:bg-on-surface-variant/8"
                  }`}
                >
                  <div className="w-4 h-4 rounded-full bg-green-500" />
                  <div className="flex-1 text-left">
                    <div className="text-body-md font-medium">Online</div>
                    <div className="text-body-sm text-on-surface-variant">Visible to all contacts</div>
                  </div>
                  <CircleDot size={20} className={presence === "online" ? "text-primary" : "text-transparent"} />
                </button>

                <button
                  type="button"
                  onClick={() => setPresence("invisible")}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border transition ${
                    presence === "invisible"
                      ? "border-primary bg-primary/10"
                      : "border-outline hover:bg-on-surface-variant/8"
                  }`}
                >
                  <EyeOff size={16} className="text-on-surface-variant" />
                  <div className="flex-1 text-left">
                    <div className="text-body-md font-medium">Invisible</div>
                    <div className="text-body-sm text-on-surface-variant">Hidden from contacts (no badge)</div>
                  </div>
                  <CircleDot size={20} className={presence === "invisible" ? "text-primary" : "text-transparent"} />
                </button>

                <button
                  type="button"
                  onClick={() => setPresence("dnd")}
                  className={`w-full flex items-center gap-3 p-3 rounded-lg border transition ${
                    presence === "dnd"
                      ? "border-primary bg-primary/10"
                      : "border-outline hover:bg-on-surface-variant/8"
                  }`}
                >
                  <XCircle size={16} className="text-on-surface-variant" />
                  <div className="flex-1 text-left">
                    <div className="text-body-md font-medium">Do Not Disturb</div>
                    <div className="text-body-sm text-on-surface-variant">Auto-queue file transfers</div>
                  </div>
                  <CircleDot size={20} className={presence === "dnd" ? "text-primary" : "text-transparent"} />
                </button>
              </div>
            </div>

            {/* Bandwidth Throttle (Disabled/TODO) */}
            <div className="opacity-50">
              <label className="text-body-sm text-on-surface-variant mb-2 block">
                Bandwidth Throttle (Coming Soon)
              </label>
              <div className="flex items-center gap-3">
                <WifiOff size={20} />
                <input
                  type="range"
                  disabled
                  min="100"
                  max="10000"
                  className="flex-1"
                />
                <Wifi size={20} />
              </div>
              <div className="text-body-sm text-on-surface-variant mt-1 text-center">
                Less ← → More
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-2 mt-6">
            <mui.Button
              onClick={() => setOpen(false)}
              variant="surface"
            >
              Cancel
            </mui.Button>
            <mui.Button
              onClick={handleSave}
              variant="primary"
              filled
              disabled={displayName.trim().length === 0}
            >
              Save
            </mui.Button>
          </div>
        </mui.Dialog.Popup>
      </mui.Dialog.Portal>
    </mui.Dialog.Root>
  );
}

// Helper functions for localStorage
function getSessionPreference(key: string): any {
  try {
    const value = localStorage.getItem(`moby_pref_${key}`);
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
}

function setSessionPreference(key: string, value: any): void {
  try {
    localStorage.setItem(`moby_pref_${key}`, JSON.stringify(value));
  } catch (error) {
    console.error(`Failed to save preference ${key}:`, error);
  }
}
